import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { EnrichedVulnerability } from '@/sections/threats/types';
import type { SsvcInputs } from '@/sections/vulnerabilities/utils/ssvcDerivation.util';
import { deriveSsvcInputs } from '@/sections/vulnerabilities/utils/ssvcDerivation.util';
import type { Asset, Risk } from '@/types';
import { cn } from '@/utils/classname';

// ---------------------------------------------------------------------------
// Display labels & styles
// ---------------------------------------------------------------------------

const LABELS: Record<string, string> = {
  none: 'None',
  poc: 'PoC',
  active: 'Active',
  yes: 'Yes',
  no: 'No',
  total: 'Total',
  partial: 'Partial',
  minimal: 'Minimal',
  support: 'Support',
  essential: 'Essential',
  material: 'Material',
  irreversible: 'Irreversible',
};

const ESCALATION_LABELS: Record<number, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
};

const DECISION_STYLES: Record<
  string,
  { bg: string; text: string; ring: string }
> = {
  Track: {
    bg: 'bg-chariot-status-info/10',
    text: 'text-chariot-status-info',
    ring: 'ring-chariot-status-info/30',
  },
  'Track*': {
    bg: 'bg-chariot-status-medium/10',
    text: 'text-chariot-status-medium',
    ring: 'ring-chariot-status-medium/30',
  },
  Attend: {
    bg: 'bg-chariot-status-high/10',
    text: 'text-chariot-status-high',
    ring: 'ring-chariot-status-high/30',
  },
  Act: {
    bg: 'bg-chariot-status-critical/10',
    text: 'text-chariot-status-critical',
    ring: 'ring-chariot-status-critical/30',
  },
};

// ---------------------------------------------------------------------------
// Step card components
// ---------------------------------------------------------------------------

interface StepProps {
  label: string;
  value: string;
  sublabel?: string;
}

function StepNode({ label, value, sublabel }: StepProps) {
  return (
    <div className="border-chariot-brand-primary/40 bg-chariot-background-secondary flex min-w-28 flex-col items-center rounded-lg border px-4 py-3">
      <span className="text-chariot-text-secondary text-[10px] font-medium tracking-wider uppercase">
        {label}
      </span>
      <span className="text-chariot-text-primary text-sm font-semibold">
        {value}
      </span>
      {sublabel && (
        <span className="text-chariot-text-secondary mt-0.5 text-[10px]">
          {sublabel}
        </span>
      )}
    </div>
  );
}

function DecisionNode({ decision }: { decision: string }) {
  const style = DECISION_STYLES[decision];
  return (
    <div
      className={cn(
        'flex min-w-32 flex-col items-center rounded-lg border-2 px-5 py-3 ring-4',
        style?.bg,
        style?.text,
        style?.ring,
        'border-current'
      )}
    >
      <span className="text-[10px] font-medium tracking-wider uppercase opacity-70">
        Decision
      </span>
      <span className="text-lg font-bold">{decision}</span>
    </div>
  );
}

function Arrow() {
  return (
    <div className="text-chariot-brand-primary flex items-center px-1">
      <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
        <path
          d="M0 6H22M22 6L17 1M22 6L17 11"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-asset decision path
// ---------------------------------------------------------------------------

function AssetDecisionPath({
  inputs,
  decision,
}: {
  inputs: SsvcInputs;
  decision: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-y-4">
        <StepNode
          label="Exploitation"
          value={LABELS[inputs.exploitation]}
          sublabel={
            inputs.exploitation === 'active' ? 'KEV / validated' : undefined
          }
        />
        <Arrow />
        <StepNode
          label="Automatable"
          value={LABELS[inputs.automatable]}
          sublabel={
            inputs.automatable === 'yes' ? 'Network + Low AC' : undefined
          }
        />
        <Arrow />
        <StepNode label="Tech. Impact" value={LABELS[inputs.technicalImpact]} />
        <Arrow />
        <StepNode
          label="Escalation"
          value={ESCALATION_LABELS[inputs.escalationLevel]}
          sublabel={`${LABELS[inputs.missionPrevalence]} / ${LABELS[inputs.publicWellBeing]}`}
        />
        <Arrow />
        <DecisionNode decision={decision} />
      </div>

      <div className="border-chariot-stroke flex flex-col gap-3 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <span className="text-chariot-text-secondary text-xs font-medium">
            Coordinator base decision
          </span>
          <span className="text-chariot-text-primary text-xs font-semibold">
            {inputs.coordinatorDecision}
          </span>
        </div>
        <div className="border-chariot-stroke border-t" />
        <div className="flex items-center justify-between">
          <span className="text-chariot-text-secondary text-xs font-medium">
            Deployer escalation (mission: {LABELS[inputs.missionPrevalence]},
            well-being: {LABELS[inputs.publicWellBeing]})
          </span>
          <span
            className={cn(
              'rounded px-2 py-0.5 text-xs font-bold',
              DECISION_STYLES[decision]?.bg,
              DECISION_STYLES[decision]?.text
            )}
          >
            {decision}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface SsvcDecisionTreeProps {
  risk: Risk;
  threatIntel?: EnrichedVulnerability;
  impactedAssets?: Asset[];
}

export function SsvcDecisionTree({
  risk,
  threatIntel,
  impactedAssets,
}: SsvcDecisionTreeProps) {
  const assets = impactedAssets ?? [];
  const hasAssets = assets.length > 0;

  const fallbackInputs = deriveSsvcInputs(threatIntel, risk);

  if (!fallbackInputs && !risk.ssvc_decision) {
    return (
      <div className="text-chariot-text-secondary py-8 text-center text-sm">
        No SSVC data available for this vulnerability.
      </div>
    );
  }

  if (!hasAssets) {
    const decision = fallbackInputs?.decision ?? risk.ssvc_decision ?? 'Track';
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h4 className="text-chariot-text-primary text-sm font-semibold">
            SSVC Deployer Decision Path
          </h4>
          <p className="text-chariot-text-secondary text-xs">
            Stakeholder-Specific Vulnerability Categorization — no impacted
            assets found, showing default path
          </p>
        </div>
        {fallbackInputs && (
          <AssetDecisionPath inputs={fallbackInputs} decision={decision} />
        )}
      </div>
    );
  }

  const singleAsset = assets.length === 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h4 className="text-chariot-text-primary text-sm font-semibold">
          SSVC Deployer Decision Path
        </h4>
        <p className="text-chariot-text-secondary text-xs">
          Stakeholder-Specific Vulnerability Categorization — per impacted asset
        </p>
      </div>

      {singleAsset ? (
        <SingleAssetPath
          asset={assets[0]}
          risk={risk}
          threatIntel={threatIntel}
        />
      ) : (
        <Accordion type="single" collapsible defaultValue={assets[0]?.key}>
          {assets.map(asset => (
            <AssetAccordionItem
              key={asset.key}
              asset={asset}
              risk={risk}
              threatIntel={threatIntel}
            />
          ))}
        </Accordion>
      )}
    </div>
  );
}

function SingleAssetPath({
  asset,
  risk,
  threatIntel,
}: {
  asset: Asset;
  risk: Risk;
  threatIntel?: EnrichedVulnerability;
}) {
  const mp = asset.mission_prevalence || 'minimal';
  const inputs = deriveSsvcInputs(threatIntel, risk, mp);
  if (!inputs) return null;
  const decision = inputs.decision;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="text-chariot-text-primary text-xs font-medium">
          {asset.name}
        </span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-semibold',
            DECISION_STYLES[decision]?.bg,
            DECISION_STYLES[decision]?.text
          )}
        >
          {decision}
        </span>
        <span className="text-chariot-text-secondary text-[10px]">
          Mission: {LABELS[mp]}
        </span>
      </div>
      <AssetDecisionPath inputs={inputs} decision={decision} />
    </div>
  );
}

function AssetAccordionItem({
  asset,
  risk,
  threatIntel,
}: {
  asset: Asset;
  risk: Risk;
  threatIntel?: EnrichedVulnerability;
}) {
  const mp = asset.mission_prevalence || 'minimal';
  const inputs = deriveSsvcInputs(threatIntel, risk, mp);
  if (!inputs) return null;
  const decision = inputs.decision;

  return (
    <AccordionItem value={asset.key} className="border-chariot-stroke">
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-primary text-xs font-medium">
            {asset.name}
          </span>
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold',
              DECISION_STYLES[decision]?.bg,
              DECISION_STYLES[decision]?.text
            )}
          >
            {decision}
          </span>
          <span className="text-chariot-text-secondary text-[10px]">
            Mission: {LABELS[mp]}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <AssetDecisionPath inputs={inputs} decision={decision} />
      </AccordionContent>
    </AccordionItem>
  );
}
